import React, { useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Grid,
  Drawer,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatProductType } from "../../utils/enumFormatter";
import ScatterFundYieldChart from "../PersonFinancialData/Charts/ScatterFundYieldChart";
import GemelInvestigationPanel from "../GemelData/GemelInvestigationPanel";
import { loadPinnedItems } from "../../stores/investigationFiltersStore";
import { getDefaultDisplayPreferences, stockExposureBuckets } from "../../utils/fundsFormatter";
import { ChartType } from "../GemelData/types";
import { AdjustedProductType, ProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { GemelDataFilteredView } from "../GemelData/types";
import ExposureGaugesByMimshakDataInteractive from "../PersonFinancialData/ExposureGaugesByMimshakDataInteractive";
import WeightedProductYieldsSummary from "../PersonFinancialData/Cards/AverageFundYieldsSummary";
import { formatCurrency } from "../../utils/namesFormater";
import { MimshakService } from "../../services/mimshakService";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";

interface MimshakScatterChartsProps {
  mimshakData: MimshakData[];
  title?: string;
  initialFilters?: Partial<GemelDataFilteredView>;
  onFundSelected?: (fund: FundDataBasic) => void;
  /** Where to place the fund accordion lists relative to the scatter chart. Defaults to "below". */
  fundListPosition?: "below" | "right";
  /** Sets the charts' "stock-exposure backgrounds" button */
  showStockExposureBands?: boolean;
  /** When set (and on every change of its value), scrolls the containing overlay so the yield-period buttons sit at its top edge */
  scrollToYieldButtonsKey?: string | number;
}

interface ProductTypeData {
  productType: number;
  uniqueFundIds: number[];
  recordsCount: number;
  totalFundsSum: number;
  fundAmountMap: Map<number, number>;
}

const MimshakScatterCharts: React.FC<MimshakScatterChartsProps> = ({
  mimshakData,
  title = "תרשימי פיזור לפי סוגי מוצר",
  initialFilters,
  onFundSelected,
  fundListPosition = "below",
  showStockExposureBands,
  scrollToYieldButtonsKey,
}) => {
  // always use local filters — clean defaults + pinnedItems from localStorage only
  const [localFilters, setLocalFilters] = React.useState<GemelDataFilteredView>(() => ({
    productType: ProductType.EDUCATION_FUND,
    buckets: stockExposureBuckets.map(b => b.label),
    charts: [ChartType.YieldBubbles],
    fundPopulations: [],
    yAxisField: undefined,
    handlers: [],
    subSpecializations: [],
    stockExposureRanges: [],
    foreignExposureRanges: [],
    currencyExposureRanges: [],
    displayPreferences: {
      ...getDefaultDisplayPreferences(),
      pinnedItems: loadPinnedItems(),
    },
    forGeneralPublic: false,
    includeIRA: true,
    ...initialFilters,
  }));

  const filters = localFilters;
  const updateFilters = React.useCallback((partial: Partial<GemelDataFilteredView>) => {
    setLocalFilters(prev => ({ ...prev, ...partial }));
  }, []);

  // חישוב product_type האפקטיבי — תיקון managers insurance ל-LIFE_INSURANCE_SAVINGS
  const effectiveProductType = useMemo((): number => {
    if (initialFilters?.productType != null) return initialFilters.productType;
    const first = mimshakData[0];
    if (!first) return ProductType.EDUCATION_FUND;
    if (
      first.product_type === ProductType.PURE_SAVINGS_POLICY &&
      [AdjustedProductType.ACTIVE_MANAGERS_INSURANCE, AdjustedProductType.INACTIVE_MANAGERS_INSURANCE].includes(first.adjusted_product_type as AdjustedProductType)
    ) {
      return ProductType.LIFE_INSURANCE_SAVINGS;
    }
    return first.product_type;
  }, [mimshakData, initialFilters?.productType]);

  // עיבוד הנתונים - קיבוץ לפי product_type ואיסוף fund IDs ייחודיים עם סכומים
  const productTypeData = useMemo(() => {
    const groupedData: { [key: number]: ProductTypeData } = {};

    mimshakData.forEach((item) => {
      const { funds_sum, data } = item;
      const product_type = effectiveProductType;

      if (!groupedData[product_type]) {
        groupedData[product_type] = {
          productType: product_type,
          uniqueFundIds: [],
          recordsCount: 0,
          totalFundsSum: 0,
          fundAmountMap: new Map<number, number>(),
        };
      }

      // חישוב סכומים לפי מסלול מתוך ה-data
      if (data?.products) {
        const fundAmounts = MimshakService.aggregateAmountsByFundIdFromProducts(data.products);

        fundAmounts.forEach((amount, fundId) => {
          if (!groupedData[product_type].uniqueFundIds.includes(fundId)) {
            groupedData[product_type].uniqueFundIds.push(fundId);
          }
          const currentAmount = groupedData[product_type].fundAmountMap.get(fundId) || 0;
          groupedData[product_type].fundAmountMap.set(fundId, currentAmount + amount);
        });
      }

      groupedData[product_type].recordsCount++;
      groupedData[product_type].totalFundsSum += funds_sum;
    });

    return Object.values(groupedData).sort((a, b) => a.productType - b.productType);
  }, [mimshakData, effectiveProductType]);

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // סנכרון productType של הפילטר עם המוצר הראשון (רק אם לא הוגדר initialFilters.productType)
  useEffect(() => {
    if (!initialFilters?.productType && productTypeData.length > 0 && filters.productType !== productTypeData[0].productType) {
      updateFilters({ productType: productTypeData[0].productType });
    }
  }, [productTypeData, filters.productType, updateFilters, initialFilters?.productType]);

  if (!mimshakData || mimshakData.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="text.secondary">
          אין נתונים להצגה
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      {/* Drawer משמאל עם GemelInvestigationPanel */}
      {drawerOpen && (
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            width: 320,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 320,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
              bgcolor: '#f5f5f5',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
            <IconButton onClick={() => setDrawerOpen(false)} size="small" title="סגור פילטרים">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <GemelInvestigationPanel
              filters={filters}
              onFiltersChange={updateFilters}
              hideProductTypeSelection={true}
            />
          </Box>
        </Drawer>
      )}

      {/* תוכן ראשי */}
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {!drawerOpen && (
          <Button
            onClick={() => setDrawerOpen(true)}
            size="small"
            variant="outlined"
            startIcon={<TuneIcon />}
            sx={{ mb: 1 }}
          >
            פילטרים
          </Button>
        )}
       
        <Grid container spacing={3}>
          {productTypeData.map((data) => {
            // הצג רק את המוצר שמתאים ל-productType הנבחר בפילטר
            if (filters.productType !== data.productType) return null;

            // סינון הנתונים רק לסוג המוצר הנוכחי
            const currentProductData = mimshakData.filter(
              (item) => item.product_type === data.productType
            );

            return (
              <Grid item xs={12} lg={12} key={data.productType}>
                    {/* Header */}
                    <Box mb={2}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {formatProductType(data.productType)}
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={`${data.uniqueFundIds.length} מסלולים `}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                        <Chip
                          label={`${data.recordsCount.toLocaleString()} פוליסות `}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                        <Chip
                          label={formatCurrency(data.totalFundsSum)}
                          size="small"
                          variant="outlined"
                          color="success"
                        />
                      </Stack>
                    </Box>

                    {/* Chart */}
                    {data.uniqueFundIds.length > 0 ? (
                      <ScatterFundYieldChart
                        productType={data.productType}
                        highlightedFunds={data.uniqueFundIds.map(fundId => ({
                          fundId,
                          amount: data.fundAmountMap.get(fundId)
                        }))}
                        autoSelectHighlighted={true}
                        filters={filters}
                        onFiltersChange={updateFilters}
                        onFundSelected={onFundSelected}
                        editMode={!!onFundSelected}
                        fundListPosition={fundListPosition}
                        showStockExposureBands={showStockExposureBands}
                        scrollToYieldButtonsKey={scrollToYieldButtonsKey}
                      />
                    ) : (
                      <Box textAlign="center" py={4}>
                        <Typography variant="body2" color="text.secondary">
                          אין קרנות זמינות עבור סוג מוצר זה
                        </Typography>
                      </Box>
                    )}
              </Grid>
            );
          })}
        </Grid>

        {productTypeData.length === 0 && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              לא נמצאו סוגי מוצרים בנתונים
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MimshakScatterCharts;